import React, { useEffect, useRef, useState } from 'react';
import { HeartPulse } from 'lucide-react';
import { getAIModelConfig, getInvokeAIProvider } from '@advisor-core/lib/aiModelConfig';
import { invokeWebAI } from '@advisor-core/lib/webAiClient';
import {
  getModelMetrics, getModelMetricsTotal, getAppVersion, getIssueTypes, getProcessModelTitle, taskJSON,
  getModelID, getFieldUsage, getStatusTypeSummary, getStatusTypeMappings, getAiConsultantContent,
  getAttributePrivacyRules, getActivityType, getActivityContext,
} from '@process-advisor/process-miner/processMiner/processMiner';
import { openProgressModal } from '@process-advisor/process-miner/api/methods/modals/openProgressModal';
import { closeProgressModal } from '@process-advisor/process-miner/api/methods/modals/closeProgressModal';
import { updateProgressModal } from '@process-advisor/process-miner/api/methods/modals/updateProgressModal';
import { startAiProgressTimer, stopAiProgressTimer, stopAllAiProgressTimers } from '@process-advisor/process-miner/consultant/aiProgressModalTimer';
import { apiProvider } from '@process-advisor/process-miner/api/apiProvider';
import { buildQuickHealthcheckPrompt } from '@process-advisor/process-miner/consultant/quickHealthcheck-prompt';
import { renderQuickHealthcheckHTML, buildQuickHealthcheckExportHTML, buildMetricPillsFromPayload } from '@process-advisor/process-miner/consultant/quickHealthcheck-renderer';
import ExportModal from '@process-advisor/process-miner/consultant/ExportModal';
import { themeAiHtml, appDefaultReportTheme } from '@process-advisor/process-miner/consultant/reportThemeUtils';
import ConsultantTabHeader, { ReadyBadge, getHeaderPrimaryButtonStyle, getHeaderSecondaryButtonStyle } from '@process-advisor/process-miner/consultant/ConsultantTabHeader';
import { SaveSnapshotButton, SaveSnapshotModal } from '@process-advisor/process-miner/consultant/ConsultantSnapshotManager';
import { saveSnapshot } from '@process-advisor/process-miner/consultant/consultantSnapshotService';
import { replaceQuotes, sleep } from '@process-advisor/process-miner/dashboard/aiinsights/aiInsights-utils';
import { saveConsultantContent } from '@process-advisor/process-miner/consultant/saveConsultantContent';
import { getCachedConsultantContent, setCachedConsultantContent, getCachedConsultantJson, setCachedConsultantJson } from '@process-advisor/process-miner/consultant/consultantContentState';
import HtmlPreviewFrame from '@process-advisor/process-miner/consultant/HtmlPreviewFrame';
import { injectOrganizationContext } from '@process-advisor/process-miner/consultant/organizationProfile';
import { buildSharedConsultantPayload } from '@process-advisor/process-miner/consultant/consultantSharedAiPayload';
import { buildConsultantRuntimeArgs } from '@process-advisor/process-miner/consultant/consultantSharedRuntimeArgs';
import { validateHealthScoreAgainstNarrative } from '@process-advisor/process-miner/consultant/consultantHealthScoreValidator';
import AiReportScopePanel, { AiScopeButton } from '@process-advisor/process-miner/consultant/AiReportScopePanel';
import AiDataPanel, { AiDataButton } from '@process-advisor/process-miner/consultant/AiDataPanel';
import { applySampleItemPrivacy } from '@process-advisor/process-miner/consultant/attributePrivacyUtils';
import { loadAiReportScopeLocal, loadAiReportScopeBackend, normalizeAiReportScope, applyAiReportScopeToEvidence } from '@process-advisor/process-miner/consultant/aiReportScope';
import { invokeProcessOpenAIWithBilling } from '@process-advisor/process-miner/consultant/aiBillingAccess';

let forgeModule = null;
const loadForgeModule = async () => { if (!forgeModule) forgeModule = await import('@forge/bridge'); return forgeModule; };
const toInvokeProvider = (p) => p === 'gemini' ? 'Gemini' : p === 'claude' ? 'Claude' : 'ChatGPT';
const COL = 'ai_quick_healthcheck';
const SLEEP_MS = 50;

const cleanAndParseJSON = (raw) => {
  let s = (raw || '{}')
    .replace(/^```json\s*/i, '')
    .replace(/^```\s*/i, '')
    .replace(/```\s*$/i, '')
    .trim();

  const lb = s.lastIndexOf('}');
  if (lb !== -1) s = s.substring(0, lb + 1);

  try {
    return JSON.parse(s);
  } catch (_) {
    // Some model responses are valid-looking JSON but get clipped near the end.
    // Match Process Health's defensive parser so Quick Healthcheck behaves the same way.
  }

  const opens = [];
  for (const ch of s) {
    if (ch === '{' || ch === '[') opens.push(ch);
    else if (ch === '}' || ch === ']') opens.pop();
  }

  let repaired = s.replace(/,?\s*\"[^\"]*\"?\s*:?\s*[^,\}\]]*$/, '');
  for (let i = opens.length - 1; i >= 0; i--) repaired += opens[i] === '{' ? '}' : ']';
  return JSON.parse(repaired);
};

const QuickHealthcheck = ({ aiModelConfig }) => {
  const modelId = getModelID();
  const [isGenerating, setIsGenerating] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [html, setHtml] = useState(null);
  const [requestId, setRequestId] = useState(null);
  const [forgeStatus, setForgeStatus] = useState(null);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [snapshotModalOpen, setSnapshotModalOpen] = useState(false);
  const [snapshotSaving, setSnapshotSaving] = useState(false);
  const [showAiScopePanel, setShowAiScopePanel] = useState(false);
  const [showAiDataPanel, setShowAiDataPanel] = useState(false);
  const [aiReportScope, setAiReportScope] = useState(() => normalizeAiReportScope(loadAiReportScopeLocal(modelId, 'quickHealthcheck') || {}));
  const [aiReportScopeSource, setAiReportScopeSource] = useState(() => loadAiReportScopeLocal(modelId, 'quickHealthcheck') ? 'local' : 'none');
  const effectiveAiReportScope = normalizeAiReportScope(aiReportScope || {});
  const pollRef = useRef(null);
  const progressSecondsRef = useRef(0);
  const aiProgressTimerRef = useRef(null);
  const requestDebugRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    loadAiReportScopeBackend(modelId, 'quickHealthcheck').then((remote) => {
      if (cancelled) return;
      if (remote?.scope) {
        setAiReportScope(normalizeAiReportScope(remote.scope));
        setAiReportScopeSource(remote.source || 'model');
      } else {
        const local = loadAiReportScopeLocal(modelId, 'quickHealthcheck');
        setAiReportScope(normalizeAiReportScope(local || {}));
        setAiReportScopeSource(local ? 'local' : 'none');
      }
    });
    return () => { cancelled = true; };
  }, [modelId]);

  const handleAiScopeChange = (nextScope, sourceHint) => {
    setAiReportScope(normalizeAiReportScope(nextScope));
    if (sourceHint) setAiReportScopeSource(sourceHint);
  };

  const getAiScopeButtonState = () => {
    if (!effectiveAiReportScope.enabled) return 'off';
    if (aiReportScopeSource === 'global') return 'default';
    return 'report';
  };

  useEffect(() => {
    const cached = getCachedConsultantContent({ modelId, column: COL });
    const dbHtml = getAiConsultantContent ? getAiConsultantContent(COL) : null;
    const saved = cached || dbHtml || null;
    if (saved) {
      setHtml(saved);
      setGenerated(true);
      if (!cached) setCachedConsultantContent({ modelId, column: COL, html: saved });
    }
  }, [modelId]);

  useEffect(() => {
    if (!requestId || !isGenerating) return;
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { invoke } = await loadForgeModule();
        const sd = await invoke('checkStatus', { requestId });
        setForgeStatus(sd);
        progressSecondsRef.current += 1;
        updateProgressModal({ status: 'Generating quick healthcheck — ' + progressSecondsRef.current + 's', ai: true });
        if (sd?.status === 'completed') {
          clearInterval(pollRef.current); pollRef.current = null;
          const raw = sd?.result || '{}';
          const json = cleanAndParseJSON(typeof raw === 'string' ? raw : JSON.stringify(raw));
          replaceQuotes(json);
          const validatedJson = validateHealthScoreAgainstNarrative(json, requestDebugRef.current?.inputPayload || null);
          validatedJson.metric_pills = buildMetricPillsFromPayload(requestDebugRef.current?.inputPayload || null);
          console.log('[PA Health Debug][Quick][completed]', {
                  signature: requestDebugRef.current?.signature || null,
            canonicalHealth: requestDebugRef.current?.canonicalHealth || null,
            actualOverallHealth: validatedJson?.overall_health || json?.overall_health || null,
            forgeStatus: sd?.status || null,
          });
          const rendered = themeAiHtml(renderQuickHealthcheckHTML(validatedJson, getProcessModelTitle()), appDefaultReportTheme());
          setHtml(rendered); setGenerated(true);
          setCachedConsultantContent({ modelId, column: COL, html: rendered });
          setCachedConsultantJson({ modelId, column: COL, json: validatedJson });
          saveConsultantContent({ modelId, column: COL, html: rendered, AppVersion: getAppVersion() });
          setIsGenerating(false); closeProgressModal();
        }
        if (sd?.status === 'failed') {
          clearInterval(pollRef.current); pollRef.current = null;
          setIsGenerating(false); closeProgressModal();
        }
      } catch (e) {
        stopAiProgressTimer(aiProgressTimerRef.current); aiProgressTimerRef.current = null;
        clearInterval(pollRef.current); pollRef.current = null;
        setIsGenerating(false); closeProgressModal();
      }
    }, 1000);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [requestId, isGenerating, modelId]);

  const buildArgs = () => {
    const { runtimeArgs, debug } = buildConsultantRuntimeArgs({
      issueTypes: getIssueTypes(),
      metrics: getModelMetrics(),
      metricsTotal: typeof getModelMetricsTotal === 'function' ? getModelMetricsTotal() : null,
      processSteps: apiProvider.processStepsTable() || [],
      variants: apiProvider.variantsTable() || [],
      rawSampleItems: taskJSON(1000),
      reportScope: effectiveAiReportScope,
      privacyRules: getAttributePrivacyRules(),
      fieldUsage: getFieldUsage ? getFieldUsage() : {},
      statusTypeSummary: getStatusTypeSummary ? getStatusTypeSummary() : null,
      statusTypeMappings: getStatusTypeMappings ? getStatusTypeMappings() : [],
      activityType: typeof getActivityType === 'function' ? getActivityType() : null,
      activityContext: typeof getActivityContext === 'function' ? getActivityContext() : null,
    });
    const inputPayload = buildSharedConsultantPayload(runtimeArgs);
    return { ...runtimeArgs, inputPayload, debug };
  };

  const generate = async () => {
    if (isGenerating) return;
    stopAllAiProgressTimers();
    setIsGenerating(true); setGenerated(false); setHtml(null);
    progressSecondsRef.current = 0;
    await openProgressModal({ status: 'Generating Quick Healthcheck…' });
    await sleep(SLEEP_MS);
    const args = buildArgs();
    requestDebugRef.current = {
      signature: args.debug.signature,
      canonicalHealth: args.inputPayload?.canonical_health || null,
      summary: args.debug.summary,
      inputPayload: args.inputPayload || null,
    };
    updateProgressModal({ status: 'Generating Quick Healthcheck…', ai: true });
    const prompt = await injectOrganizationContext(buildQuickHealthcheckPrompt(args));
    const { provider, model, temperature } = getAIModelConfig(aiModelConfig);
    const AppVersion = getAppVersion();
    if (AppVersion === 'FORGE') {
      try {
        const { invoke } = await loadForgeModule();
        const response = await invokeProcessOpenAIWithBilling({
          invoke,
          reportLabel: 'Quick Healthcheck',
          payload: { prompt, aiProvider: toInvokeProvider(provider), model, temperature },
        });
        setRequestId(response.requestId);
        setForgeStatus({ status: 'pending' });
      } catch (e) {
        console.error('[QuickHealthcheck] Forge error:', e);
        setIsGenerating(false);
        closeProgressModal();
      }
      return;
    }

    if (AppVersion === 'WEB') {
      aiProgressTimerRef.current = await startAiProgressTimer('Generating Quick Healthcheck');
      try {
        const result = await invokeWebAI({
          prompt,
          reportLabel: 'Quick Healthcheck',
          temperature,
          responseFormat: 'json',
          modelId,
        });
        const json = cleanAndParseJSON(result?.text || '{}');
        replaceQuotes(json);
        const validatedJson = validateHealthScoreAgainstNarrative(json, requestDebugRef.current?.inputPayload || null);
        validatedJson.metric_pills = buildMetricPillsFromPayload(requestDebugRef.current?.inputPayload || null);
        const rendered = themeAiHtml(renderQuickHealthcheckHTML(validatedJson, getProcessModelTitle()), appDefaultReportTheme());
        setHtml(rendered); setGenerated(true);
        setCachedConsultantContent({ modelId, column: COL, html: rendered });
        setCachedConsultantJson({ modelId, column: COL, json: validatedJson });
        saveConsultantContent({ modelId, column: COL, html: rendered, AppVersion });
      } catch (e) {
        console.error('[QuickHealthcheck] Web AI error:', e);
      } finally {
        stopAiProgressTimer(aiProgressTimerRef.current); aiProgressTimerRef.current = null;
        setIsGenerating(false);
        closeProgressModal();
      }
      return;
    }

    // ServiceNow / non-Forge path.
    // Process Health already has a direct Gemini path. Quick Healthcheck was missing it,
    // so SNOW would build the prompt, then immediately stop without generating anything.
    if (AppVersion === 'SNOW') {
      const geminiKey = (typeof API_KEY !== 'undefined' && API_KEY)
        ? API_KEY
        : undefined;

      aiProgressTimerRef.current = await startAiProgressTimer('Generating Quick Healthcheck');

      try {
        const res = await fetch(
          `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${geminiKey}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              contents: [{ parts: [{ text: prompt + '\n\nYou must output valid JSON. Do not include ``` at beginning and end.' }] }],
              generationConfig: { responseMimeType: 'application/json', maxOutputTokens: 32768 },
            }),
          }
        );

        if (!res.ok) {
          const errorText = await res.text().catch(() => '');
          throw new Error(`Gemini request failed: ${res.status} ${res.statusText} ${errorText}`);
        }

        const data = await res.json();
        const raw = data?.candidates?.[0]?.content?.parts?.[0]?.text || '{}';
        const json = cleanAndParseJSON(raw);
        replaceQuotes(json);

        const validatedJson = validateHealthScoreAgainstNarrative(json, requestDebugRef.current?.inputPayload || null);
        validatedJson.metric_pills = buildMetricPillsFromPayload(requestDebugRef.current?.inputPayload || null);
        console.log('[PA Health Debug][Quick][completed]', {
          signature: requestDebugRef.current?.signature || null,
          canonicalHealth: requestDebugRef.current?.canonicalHealth || null,
          actualOverallHealth: validatedJson?.overall_health || json?.overall_health || null,
          forgeStatus: 'completed-snow',
        });

        const rendered = themeAiHtml(renderQuickHealthcheckHTML(validatedJson, getProcessModelTitle()), appDefaultReportTheme());
        setHtml(rendered);
        setGenerated(true);
        setCachedConsultantContent({ modelId, column: COL, html: rendered });
        setCachedConsultantJson({ modelId, column: COL, json: validatedJson });
        saveConsultantContent({ modelId, column: COL, html: rendered, AppVersion });
      } catch (e) {
        console.error('[QuickHealthcheck] direct API error:', e);
      } finally {
        stopAiProgressTimer(aiProgressTimerRef.current); aiProgressTimerRef.current = null;
        setIsGenerating(false);
        closeProgressModal();
      }
      return;
    }

    console.warn('[QuickHealthcheck] Unsupported AppVersion:', AppVersion);
    setIsGenerating(false);
    closeProgressModal();
  };

  const handleSaveSnapshot = async (description) => {
    setSnapshotSaving(true);
    try {
      if (!html) return;
      const cachedJson = getCachedConsultantJson({ modelId, column: COL });
      const snapshotHtml = cachedJson ? themeAiHtml(buildQuickHealthcheckExportHTML(cachedJson, getProcessModelTitle()), appDefaultReportTheme()) : html;
      const result = await saveSnapshot({
        modelId,
        tab: 'quickHealthcheck',
        tabLabel: 'Quick Healthcheck',
        description,
        html: snapshotHtml,
        AppVersion: getAppVersion(),
      });
      if (!result?.success) throw new Error('Snapshot was not saved');
      setSnapshotModalOpen(false);
    } catch (error) {
      console.error('Advisor snapshot save failed', error);
      window.alert(`Unable to save Advisor Snapshot: ${error.message || error}`);
    } finally {
      setSnapshotSaving(false);
    }
  };

  const exportHtml = (themeId = appDefaultReportTheme()) => {
    const cachedJson = getCachedConsultantJson({ modelId, column: COL });
    const output = cachedJson ? themeAiHtml(buildQuickHealthcheckExportHTML(cachedJson, getProcessModelTitle()), themeId) : html;
    if (!output) return;
    const blob = new Blob([output], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob); const a = document.createElement('a');
    a.href = url; a.download = `quick-healthcheck-${(getProcessModelTitle() || 'report').replace(/\s+/g, '-').toLowerCase()}.html`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); setTimeout(() => URL.revokeObjectURL(url), 1000); setIsExportModalOpen(false);
  };
  const exportPdf = (themeId = appDefaultReportTheme()) => {
    const cachedJson = getCachedConsultantJson({ modelId, column: COL });
    const output = cachedJson ? themeAiHtml(buildQuickHealthcheckExportHTML(cachedJson, getProcessModelTitle()), themeId) : html;
    if (!output) return; const win = window.open('', '_blank', 'width=1400,height=900'); if (!win) return;
    win.document.open(); win.document.write(output); win.document.close(); win.focus(); setTimeout(() => win.print(), 300); setIsExportModalOpen(false);
  };
  const exportJson = () => {
    const cachedJson = getCachedConsultantJson({ modelId, column: COL });
    if (!cachedJson) return; const blob = new Blob([JSON.stringify(cachedJson, null, 2)], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob); const a = document.createElement('a');
    a.href = url; a.download = `quick-healthcheck-${(getProcessModelTitle() || 'report').replace(/\s+/g, '-').toLowerCase()}.json`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); setTimeout(() => URL.revokeObjectURL(url), 1000); setIsExportModalOpen(false);
  };

  const exportPptx = async (themeId = appDefaultReportTheme()) => {
    const contentJson = getCachedConsultantJson({ modelId, column: COL });
    if (!contentJson) return;
    try {
      // Lazy import keeps pptxgenjs out of the main bundle until first use.
      const { buildAiContentPptx } = await import('@process-advisor/process-miner/consultant/aiContent-pptx');
      const pptx = buildAiContentPptx('quickHealthcheck', contentJson, getProcessModelTitle(), { theme: themeId });
      await pptx.writeFile({ fileName: `quick-healthcheck-${(getProcessModelTitle() || 'report').replace(/\s+/g, '-').toLowerCase()}.pptx` });
    } catch (error) {
      console.error('PPTX export failed', error);
    }
    setIsExportModalOpen(false);
  };

  const getScopedTaskRowsForAiData = () => applyAiReportScopeToEvidence({ rawSampleItems: taskJSON(1000), processSteps: apiProvider.processStepsTable() || [], variants: apiProvider.variantsTable() || [], scope: effectiveAiReportScope }).rawSampleItems;
  const aiDataPrivacyCounts = React.useMemo(() => {
    // Do not call taskJSON()/processStepsTable()/variantsTable() while simply opening Advisor.
    // On large ServiceNow models those helpers can scan/sort large arrays and block the UI.
    const rules = getAttributePrivacyRules ? getAttributePrivacyRules() : {};
    return Object.values(rules || {}).reduce((acc, rule) => {
      if (rule === 'exclude') acc.excluded += 1;
      if (rule === 'anonymize') acc.anonymized += 1;
      return acc;
    }, { excluded: 0, anonymized: 0 });
  }, [modelId, showAiDataPanel]);

  return (
    <div style={{ backgroundColor: '#f8f9fa', padding: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        <ConsultantTabHeader
          title="Quick Healthcheck"
          subtitle="A short AI summary of strengths, issues, risks, and quick wins"
          icon={<HeartPulse size={20} color="white" />}
          iconBackground="linear-gradient(135deg, #0052CC 0%, #2684FF 100%)"
          titleColor="#0052CC"
          actions={(
            <>
              {!generated && <ReadyBadge color="#0C66E4" />}
              <AiScopeButton
                onClick={() => setShowAiScopePanel(v => !v)}
                state={getAiScopeButtonState()}
                count={(effectiveAiReportScope.excludeVariantIds?.length || 0) + (effectiveAiReportScope.excludeWorkItems?.length || 0) + (effectiveAiReportScope.excludePaths?.length || 0) + (effectiveAiReportScope.excludeFieldValues?.length || 0) + (effectiveAiReportScope.instructions?.trim() ? 1 : 0) + ((Number(effectiveAiReportScope.sampleItemLimit) || 100) !== 100 ? 1 : 0)}
              />
              <AiDataButton
                onClick={() => setShowAiDataPanel(v => !v)}
                excludedCount={aiDataPrivacyCounts.excluded}
                anonymizedCount={aiDataPrivacyCounts.anonymized}
                isActive={showAiDataPanel}
              />
              {generated && (
                <>
                  {(typeof window === "undefined" || window.__METRICUS_JIRA_PRODUCT_EDITION__ !== "free") && <SaveSnapshotButton color="#0052CC" onClick={() => setSnapshotModalOpen(true)} />}
                  <button style={getHeaderPrimaryButtonStyle('#0052CC')} onClick={() => { setHtml(null); setGenerated(false); }}>New Healthcheck</button>
                  <button style={getHeaderSecondaryButtonStyle('#00875A')} onClick={() => setIsExportModalOpen(true)}>Export...</button>
                </>
              )}
            </>
          )}
        />

        <AiReportScopePanel
          isOpen={showAiScopePanel}
          onClose={() => setShowAiScopePanel(false)}
          modelId={modelId}
          reportKey="quickHealthcheck"
          reportLabel="Quick Healthcheck"
          scope={effectiveAiReportScope}
          scopeSource={aiReportScopeSource}
          onScopeChange={handleAiScopeChange}
          onScopeSaved={handleAiScopeChange}
          getTaskRows={taskJSON}
          getVariants={() => apiProvider.variantsTable() || []}
          getProcessSteps={() => apiProvider.processStepsTable() || []}
        />
        <AiDataPanel
          isOpen={showAiDataPanel}
          onClose={() => setShowAiDataPanel(false)}
          getTaskRows={getScopedTaskRowsForAiData}
          getPrivacyRules={getAttributePrivacyRules}
          applySampleItemPrivacyFn={applySampleItemPrivacy}
        />

        {!generated && (
          <div style={{ background: '#fff', padding: '28px 32px', margin: '0 10px 10px', borderRadius: '0 0 8px 8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '2px solid #dfe1e6', borderTop: 'none' }}>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div style={{ width: 56, height: 56, margin: '0 auto 16px', background: 'linear-gradient(135deg, #0052CC 0%, #2684FF 100%)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <HeartPulse size={28} color="white" />
              </div>
              <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: '#172b4d' }}>Generate Quick Healthcheck</h3>
              <p style={{ margin: 0, fontSize: 14, color: '#5e6c84', lineHeight: 1.6, maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
                A compact, evidence-based summary of what is working, what needs attention, the main risks, and the fastest improvement opportunities. Generates in about a minute.
              </p>
            </div>

            <div style={{ textAlign: 'center' }}>
              <button onClick={generate} disabled={isGenerating} style={{ background: isGenerating ? '#97a0af' : '#0052CC', color: '#fff', border: 'none', borderRadius: 6, padding: '12px 28px', fontSize: 14, fontWeight: 700, cursor: isGenerating ? 'not-allowed' : 'pointer', boxShadow: '0 4px 12px rgba(0,82,204,0.28)' }}>
                {isGenerating ? 'Generating…' : 'Generate Healthcheck'}
              </button>
            </div>
          </div>
        )}

        {generated && html && (
          <div style={{ margin: '0 10px 10px', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #DFE1E6', background: '#fff', minHeight: 600 }}>
            <HtmlPreviewFrame html={html} title="Quick Healthcheck Preview" />
          </div>
        )}
      </div>

      <SaveSnapshotModal
        isOpen={snapshotModalOpen}
        onClose={() => setSnapshotModalOpen(false)}
        onSave={handleSaveSnapshot}
        tab="quickHealthcheck"
        tabLabel="Quick Healthcheck"
        saving={snapshotSaving}
      />
      <ExportModal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} title="Export Quick Healthcheck" onExportHTML={exportHtml} onExportPDF={exportPdf} onExportJSON={exportJson} onExportPPTX={exportPptx} />
    </div>
  );
};

export default QuickHealthcheck;
